import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, X, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MatchingGameViewer() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [gameSets, setGameSets] = useState([]);
  const [selectedSetIndex, setSelectedSetIndex] = useState(0);
  const [shuffledTerms, setShuffledTerms] = useState([]);
  const [shuffledDefinitions, setShuffledDefinitions] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [matches, setMatches] = useState([]); // Array of {termIndex, definitionIndex}
  const [loading, setLoading] = useState(true);
  const [gameCompleted, setGameCompleted] = useState(false);

  useEffect(() => {
    loadGame();
  }, [id]);

  const shufflePairs = (pairs) => {
    const shuffledT = [...pairs].sort(() => Math.random() - 0.5);
    const shuffledD = [...pairs].sort(() => Math.random() - 0.5);
    setShuffledTerms(shuffledT.map(p => p.term));
    setShuffledDefinitions(shuffledD.map(p => p.definition));
  };

  const loadGame = async () => {
    try {
      const response = await axios.get(`${API}/matching-games/${id}`);
      setGame(response.data);
      
      const sets = response.data.sets && response.data.sets.length > 0
        ? response.data.sets
        : [response.data.pairs];
      setGameSets(sets);
      shufflePairs(sets[0]);
    } catch (error) {
      console.error('Failed to load matching game:', error);
    } finally {
      setLoading(false);
    }
  };

  const activePairs = gameSets[selectedSetIndex] || [];

  const handleSelectSet = (setIndex) => {
    if (setIndex === selectedSetIndex) return;
    setSelectedSetIndex(setIndex);
    shufflePairs(gameSets[setIndex]);
    setMatches([]);
    setSelectedTerm(null);
    setSelectedDefinition(null);
    setGameCompleted(false);
  };

  const handleTermClick = (index) => {
    if (matches.some(m => m.termIndex === index)) return; // Already matched
    setSelectedTerm(index);
    checkMatch(index, selectedDefinition);
  };

  const handleDefinitionClick = (index) => {
    if (matches.some(m => m.definitionIndex === index)) return; // Already matched
    setSelectedDefinition(index);
    checkMatch(selectedTerm, index);
  };

  const checkMatch = (termIdx, defIdx) => {
    if (termIdx === null || defIdx === null) return;
    
    const term = shuffledTerms[termIdx];
    const definition = shuffledDefinitions[defIdx];
    
    // Find if they match in the active set's pairs
    const isMatch = activePairs.some(pair => 
      pair.term === term && pair.definition === definition
    );
    
    if (isMatch) {
      setMatches([...matches, { termIndex: termIdx, definitionIndex: defIdx }]);
      toast.success('Correct match!');
      
      // Check if game is completed
      if (matches.length + 1 === activePairs.length) {
        setGameCompleted(true);
        setTimeout(() => toast.success('🎉 Congratulations! You matched all pairs!'), 500);
      }
    } else {
      toast.error('Not a match, try again!');
    }
    
    // Reset selections
    setSelectedTerm(null);
    setSelectedDefinition(null);
  };

  const handleReset = () => {
    // Reshuffle
    shufflePairs(activePairs);
    setMatches([]);
    setSelectedTerm(null);
    setSelectedDefinition(null);
    setGameCompleted(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <div className="text-2xl text-[#6B4423]">Loading matching game...</div>
        </div>
      </Layout>
    );
  }

  if (!game) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl retro-title text-[#2C1810] mb-4">Matching Game Not Found</h1>
          <Link to="/matching-center" className="retro-button">
            Back to Matching Games
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/matching-center" className="inline-flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-4">
            <ArrowLeft className="w-5 h-5" />
            Back to Matching Games
          </Link>
          <h1 className="text-4xl retro-title text-[#2C1810] mb-2">{game.test_title}</h1>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-[#4D9E7C] text-white text-sm font-bold rounded">
              {game.tier}
            </span>
            <span className="px-3 py-1 bg-[#E8934D] text-white text-sm font-bold rounded">
              {game.category}
            </span>
          </div>
        </div>

        {/* Set Selector */}
        {gameSets.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="matching-set-selector">
            {gameSets.map((_, setIdx) => (
              <button
                key={setIdx}
                onClick={() => handleSelectSet(setIdx)}
                className={`px-4 py-2 rounded-lg font-bold transition-all ${
                  selectedSetIndex === setIdx
                    ? 'bg-[#4D9E7C] text-white scale-105 shadow-lg'
                    : 'bg-[#F4E4C1] text-[#2C1810] hover:bg-[#E8B67A]'
                }`}
                data-testid={`matching-set-button-${setIdx}`}
              >
                Set {setIdx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Instructions */}
        <div className="cassette-card p-6 mb-8">
          <p className="text-[#2C1810] text-center text-lg">
            <strong>Match the terms with their definitions.</strong> Click a term, then click its matching definition.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <span className="text-lg font-bold text-[#2C1810]">
              Matched: {matches.length} / {activePairs.length}
            </span>
            <button onClick={handleReset} className="retro-button-small flex items-center justify-center gap-2 w-full sm:w-auto">
              <RotateCw className="w-4 h-4" />
              Reset Game
            </button>
          </div>
        </div>

        {/* Matching Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Terms Column */}
          <div className="space-y-4">
            <h3 className="text-2xl retro-title text-[#2C1810] text-center mb-4">Terms</h3>
            {shuffledTerms.map((term, index) => {
              const isMatched = matches.some(m => m.termIndex === index);
              const isSelected = selectedTerm === index;
              
              return (
                <button
                  key={index}
                  onClick={() => handleTermClick(index)}
                  disabled={isMatched}
                  className={`matching-item-card w-full p-4 rounded-lg text-left transition-all font-semibold ${
                    isMatched 
                      ? 'bg-green-100 border-2 border-green-500 text-black cursor-not-allowed' 
                      : isSelected
                      ? 'bg-blue-500 text-white border-2 border-blue-600 shadow-lg'
                      : 'cassette-card hover:shadow-lg hover:scale-105 text-[#2C1810]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{term}</span>
                    {isMatched && <CheckCircle className="w-5 h-5 text-green-600" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Definitions Column */}
          <div className="space-y-4">
            <h3 className="text-2xl retro-title text-[#2C1810] text-center mb-4">Definitions</h3>
            {shuffledDefinitions.map((definition, index) => {
              const isMatched = matches.some(m => m.definitionIndex === index);
              const isSelected = selectedDefinition === index;
              
              return (
                <button
                  key={index}
                  onClick={() => handleDefinitionClick(index)}
                  disabled={isMatched}
                  className={`matching-item-card w-full p-4 rounded-lg text-left transition-all ${
                    isMatched 
                      ? 'bg-green-100 border-2 border-green-500 text-black cursor-not-allowed' 
                      : isSelected
                      ? 'bg-blue-500 text-white border-2 border-blue-600 shadow-lg'
                      : 'cassette-card hover:shadow-lg hover:scale-105 text-[#2C1810]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{definition}</span>
                    {isMatched && <CheckCircle className="w-5 h-5 text-green-600" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Completion Message */}
        {gameCompleted && (
          <div className="mt-12 cassette-card p-8 bg-gradient-to-r from-green-100 to-green-200 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl retro-title text-green-800 mb-4">Excellent Work!</h2>
            <p className="text-lg text-green-700 mb-6">You've successfully matched all pairs!</p>
            <button onClick={handleReset} className="retro-button">
              Play Again
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
